import { motion } from "motion/react";
import ImageWithFallback from "../ui/ImageWithFallback";
import ParentDogCard from "../ui/ParentDogCard";

export default function PuppiesSection({
                                           primaryColor,
                                           secondaryColor,
                                           welpenWurf,
                                           welpenWuerfe = [],
                                           elternTiere = [],
                                           styles,
                                           onPuppyInquiry,
                                       }) {
    const litters = welpenWuerfe.length
        ? welpenWuerfe
        : welpenWurf
            ? [welpenWurf]
            : [];

    if (!litters.length) return null;

    const statusBg = (status) => {
        if (!status) return "rgba(0,0,0,0.55)";

        const normalizedStatus = status.toLowerCase();

        if (normalizedStatus.includes("vergeben")) {
            return "rgba(120,120,120,0.85)";
        }

        if (normalizedStatus.includes("bleibt")) {
            return "rgba(70,120,90,0.90)";
        }

        if (normalizedStatus.includes("frei")) {
            return "rgba(70,120,90,0.90)";
        }

        return "rgba(90,60,50,0.88)";
    };

    const normalize = (value) =>
        (value ?? "").toString().trim().toLowerCase();

    const formatDate = (value) => {
        if (!value) return null;

        if (/^\d{2}\.\d{2}\.\d{4}$/.test(value)) {
            return value;
        }

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return value;

        return date.toLocaleDateString("de-DE", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const extractParentTokens = (parentsStr) => {
        const value = normalize(parentsStr);
        if (!value) return [];

        return value
            .split(/&|und|\+|,|\/|×|x/)
            .map((token) => token.trim())
            .filter(Boolean);
    };

    const getParentsToShow = (litter) => {
        if (Array.isArray(litter.parentsDogs) && litter.parentsDogs.length) {
            return litter.parentsDogs;
        }

        const directParents = [litter.mother, litter.father].filter(Boolean);

        if (directParents.length) {
            return directParents;
        }

        const parentTokens = extractParentTokens(litter.parents);

        const matchesByName =
            parentTokens.length > 0
                ? elternTiere.filter((tier) => {
                    const name = normalize(tier.name);
                    const callName = normalize(tier.callName);

                    return parentTokens.some(
                        (token) =>
                            token &&
                            (name.includes(token) || callName.includes(token))
                    );
                })
                : [];

        if (matchesByName.length >= 2) {
            return matchesByName;
        }

        const motherFallback = elternTiere.find(
            (tier) => normalize(tier.role) === "mutter"
        );

        const fatherFallback = elternTiere.find(
            (tier) => normalize(tier.role) === "vater"
        );

        return [motherFallback, fatherFallback].filter(Boolean);
    };

    return (
        <section
            style={{
                ...styles.section,
                overflowX: "hidden",
            }}
        >
            <motion.h2
                style={{
                    ...styles.sectionTitle,
                    color: primaryColor,
                    fontSize: "clamp(2rem, 7vw, 3rem)",
                    padding: "0 1rem",
                    wordBreak: "break-word",
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                Unsere Welpen
            </motion.h2>

            <div
                style={{
                    display: "grid",
                    gap: "clamp(3rem, 8vw, 5rem)",
                    width: "100%",
                }}
            >
                {litters.map((litter, litterIndex) => {
                    const parentsToShow = getParentsToShow(litter);
                    const puppies = litter.puppies || [];
                    const birthDate = formatDate(litter.date || litter.birthDate);

                    return (
                        <article
                            key={litter.id ?? litter.slug ?? litterIndex}
                            style={{
                                width: "100%",
                                minWidth: 0,
                            }}
                        >
                            <motion.div
                                style={{
                                    ...(styles.wurfHeader || {}),
                                    padding: "0 clamp(1rem, 4vw, 18px)",
                                    maxWidth: 1200,
                                    margin: "0 auto",
                                }}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.8 }}
                                viewport={{ once: true }}
                            >
                                <h3
                                    style={{
                                        ...styles.wurfTitle,
                                        color: primaryColor,
                                        fontFamily: "'Playfair Display', serif",
                                        fontSize: "clamp(1.8rem, 7vw, 2.6rem)",
                                        lineHeight: 1.15,
                                        marginBottom: "1rem",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {litter.name}
                                </h3>

                                <div
                                    style={{
                                        ...(styles.wurfInfo || {}),
                                        display: "flex",
                                        justifyContent: "center",
                                        flexWrap: "wrap",
                                        gap: "0.75rem",
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {birthDate && (
                                        <span
                                            style={{
                                                ...styles.wurfDate,
                                                maxWidth: "100%",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            Geboren am: {birthDate}
                                        </span>
                                    )}

                                    {(litter.count || litter.countLabel) && (
                                        <span
                                            style={{
                                                ...styles.wurfCount,
                                                maxWidth: "100%",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            {litter.count || litter.countLabel}
                                        </span>
                                    )}

                                    {litter.parents && (
                                        <span
                                            style={{
                                                ...styles.wurfParents,
                                                maxWidth: "100%",
                                                overflowWrap: "anywhere",
                                            }}
                                        >
                                            Eltern: {litter.parents}
                                        </span>
                                    )}
                                </div>
                            </motion.div>

                            {parentsToShow.length > 0 && (
                                <div
                                    style={{
                                        margin: "10px auto 34px",
                                        maxWidth: 1200,
                                        padding: "0 clamp(1rem, 4vw, 18px)",
                                        width: "100%",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    <motion.h3
                                        style={{
                                            ...styles.subsectionTitle,
                                            color: secondaryColor,
                                            marginTop: 8,
                                            marginBottom: 18,
                                            fontSize: "clamp(1.45rem, 6vw, 2.2rem)",
                                            lineHeight: 1.2,
                                            padding: "0 0.25rem",
                                        }}
                                        initial={{ opacity: 0, y: 16 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.7 }}
                                        viewport={{ once: true }}
                                    >
                                        Eltern des Wurfs
                                    </motion.h3>

                                    <motion.p
                                        style={{
                                            ...styles.elternText,
                                            maxWidth: 900,
                                            margin: "0 auto 18px",
                                            textAlign: "center",
                                            lineHeight: 1.7,
                                            opacity: 0.92,
                                            fontSize: "clamp(1rem, 2.6vw, 1.1rem)",
                                        }}
                                        initial={{ opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        transition={{ duration: 0.7 }}
                                        viewport={{ once: true }}
                                    >
                                        Die Eltern unseres Wurfs im Überblick –
                                        charakterstark, gesundheitsgeprüft und
                                        sorgfältig ausgewählt.
                                    </motion.p>

                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns:
                                                "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                                            gap: "clamp(1rem, 3vw, 1.25rem)",
                                            alignItems: "stretch",
                                            width: "100%",
                                        }}
                                    >
                                        {parentsToShow.map((tier, index) => (
                                            <ParentDogCard
                                                key={tier.id ?? index}
                                                tier={tier}
                                                index={index}
                                                primaryColor={primaryColor}
                                                secondaryColor={secondaryColor}
                                                styles={styles}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(litter.groupImage ||
                                litter.group_image ||
                                litter.description) && (
                                <motion.div
                                    style={{
                                        ...(styles.wurfGruppenbildContainer || {}),
                                        padding: "0 clamp(1rem, 4vw, 18px)",
                                        maxWidth: 1200,
                                        margin: "0 auto clamp(2rem, 6vw, 3rem)",
                                        width: "100%",
                                        boxSizing: "border-box",
                                    }}
                                    initial={{ opacity: 0 }}
                                    whileInView={{ opacity: 1 }}
                                    transition={{ duration: 0.8 }}
                                    viewport={{ once: true }}
                                >
                                    {(litter.groupImage || litter.group_image) && (
                                        <div
                                            style={{
                                                width: "100%",
                                                aspectRatio:
                                                    "min(16 / 7, 100vw / 70vw)",
                                                minHeight: "clamp(220px, 55vw, 360px)",
                                                maxHeight: 520,
                                                background: "#FAFAFA",
                                                borderRadius: "clamp(16px, 4vw, 22px)",
                                                overflow: "hidden",
                                                border: "1px solid rgba(0,0,0,0.06)",
                                            }}
                                        >
                                            <ImageWithFallback
                                                src={
                                                    litter.groupImage ||
                                                    litter.group_image
                                                }
                                                alt={`${litter.name} Gruppenbild`}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    objectFit: "cover",
                                                    objectPosition: "center",
                                                    display: "block",
                                                }}
                                            />
                                        </div>
                                    )}

                                    {litter.description && (
                                        <div
                                            style={{
                                                ...(styles.wurfDescription || {}),
                                                maxWidth: 980,
                                                margin: "1.25rem auto 0",
                                                textAlign: "center",
                                                lineHeight: 1.8,
                                                fontSize:
                                                    "clamp(1rem, 2.6vw, 1.1rem)",
                                                padding: "0 0.25rem",
                                            }}
                                        >
                                            <p style={{ margin: 0 }}>
                                                {litter.description}
                                            </p>
                                        </div>
                                    )}
                                </motion.div>
                            )}

                            {puppies.length > 0 && (
                                <div
                                    style={{
                                        ...(styles.welpenGrid || {}),
                                        display: "grid",
                                        gridTemplateColumns:
                                            "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
                                        gap: "clamp(1rem, 3vw, 1.25rem)",
                                        alignItems: "stretch",
                                        padding: "0 clamp(1rem, 4vw, 18px)",
                                        maxWidth: 1200,
                                        margin: "0 auto",
                                        width: "100%",
                                        boxSizing: "border-box",
                                    }}
                                >
                                    {puppies.map((puppy, index) => {
                                        const status = puppy.status || "";
                                        const lowerStatus = status.toLowerCase();

                                        const isTaken =
                                            lowerStatus.includes("vergeben") ||
                                            lowerStatus.includes("bleibt");

                                        return (
                                            <motion.div
                                                key={puppy.id ?? index}
                                                initial={{ opacity: 0, y: 20 }}
                                                whileInView={{ opacity: 1, y: 0 }}
                                                transition={{
                                                    duration: 0.45,
                                                    delay: Math.min(index * 0.06, 0.18),
                                                }}
                                                viewport={{ once: true }}
                                                whileHover={{
                                                    y: -8,
                                                    boxShadow:
                                                        "0 15px 30px rgba(0,0,0,0.10)",
                                                }}
                                                style={{
                                                    ...(styles.welpeCard || {}),
                                                    borderRadius: 18,
                                                    overflow: "hidden",
                                                    background: "#fff",
                                                    border: "1px solid rgba(0,0,0,0.06)",
                                                    boxShadow:
                                                        "0 10px 26px rgba(0,0,0,0.06)",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    minWidth: 0,
                                                }}
                                            >
                                                {puppy.image && (
                                                    <div
                                                        style={{
                                                            position: "relative",
                                                            width: "100%",
                                                            aspectRatio: "4 / 3",
                                                            minHeight: 210,
                                                            background: "#FAFAFA",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            padding: 10,
                                                            boxSizing: "border-box",
                                                        }}
                                                    >
                                                        <ImageWithFallback
                                                            src={puppy.image}
                                                            alt={puppy.name}
                                                            style={{
                                                                width: "100%",
                                                                height: "100%",
                                                                objectFit: "contain",
                                                                objectPosition: "center",
                                                                borderRadius: 14,
                                                                display: "block",
                                                            }}
                                                        />

                                                        {status && (
                                                            <div
                                                                style={{
                                                                    position: "absolute",
                                                                    top: 12,
                                                                    right: 12,
                                                                    maxWidth:
                                                                        "calc(100% - 24px)",
                                                                    padding: "6px 10px",
                                                                    borderRadius: 999,
                                                                    fontSize: 12,
                                                                    fontWeight: 800,
                                                                    color: "#fff",
                                                                    background:
                                                                        statusBg(status),
                                                                    border:
                                                                        "1px solid rgba(255,255,255,0.25)",
                                                                    backdropFilter:
                                                                        "blur(6px)",
                                                                    overflowWrap:
                                                                        "anywhere",
                                                                }}
                                                            >
                                                                {status}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}

                                                {!puppy.image && status && (
                                                    <div
                                                        style={{
                                                            padding: "14px 14px 0",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                display: "inline-block",
                                                                padding: "6px 10px",
                                                                borderRadius: 999,
                                                                fontSize: 12,
                                                                fontWeight: 800,
                                                                color: "#fff",
                                                                background:
                                                                    statusBg(status),
                                                            }}
                                                        >
                                                            {status}
                                                        </span>
                                                    </div>
                                                )}

                                                <div
                                                    style={{
                                                        ...(styles.welpeInfo || {}),
                                                        padding: "14px 14px 16px",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        flexGrow: 1,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    <h3
                                                        style={{
                                                            color: primaryColor,
                                                            fontFamily:
                                                                "'Playfair Display', serif",
                                                            margin: "0 0 10px",
                                                            fontSize:
                                                                "clamp(1.2rem, 4vw, 1.45rem)",
                                                            lineHeight: 1.2,
                                                            overflowWrap: "anywhere",
                                                        }}
                                                    >
                                                        {puppy.name}
                                                    </h3>

                                                    <div
                                                        style={{
                                                            display: "grid",
                                                            gap: 8,
                                                            marginBottom: 12,
                                                        }}
                                                    >
                                                        {puppy.gender && (
                                                            <InfoRow
                                                                label="Geschlecht"
                                                                value={puppy.gender}
                                                            />
                                                        )}

                                                        {puppy.color && (
                                                            <InfoRow
                                                                label="Farbe"
                                                                value={puppy.color}
                                                            />
                                                        )}

                                                        {puppy.character && (
                                                            <InfoRow
                                                                label="Charakter"
                                                                value={puppy.character}
                                                            />
                                                        )}
                                                    </div>

                                                    <motion.button
                                                        style={{
                                                            ...(styles.button || {}),
                                                            width: "100%",
                                                            backgroundColor: isTaken
                                                                ? "#A9A9A9"
                                                                : primaryColor,
                                                            opacity: isTaken ? 0.7 : 1,
                                                            cursor: isTaken
                                                                ? "not-allowed"
                                                                : "pointer",
                                                            marginTop: "auto",
                                                            minHeight: 44,
                                                        }}
                                                        whileHover={
                                                            !isTaken
                                                                ? {
                                                                    scale: 1.03,
                                                                    backgroundColor:
                                                                    secondaryColor,
                                                                }
                                                                : { scale: 1 }
                                                        }
                                                        whileTap={
                                                            !isTaken
                                                                ? { scale: 0.97 }
                                                                : { scale: 1 }
                                                        }
                                                        onClick={() =>
                                                            !isTaken &&
                                                            onPuppyInquiry?.()
                                                        }
                                                        disabled={isTaken}
                                                    >
                                                        {isTaken
                                                            ? "Vergeben"
                                                            : "Anfragen"}
                                                    </motion.button>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </article>
                    );
                })}
            </div>

            <motion.div
                style={{
                    ...styles.welpenNote,
                    padding: "clamp(1.5rem, 5vw, 2rem) clamp(1rem, 4vw, 18px)",
                    maxWidth: 980,
                    margin: "clamp(2rem, 6vw, 3.5rem) auto 0",
                    textAlign: "center",
                    lineHeight: 1.8,
                    boxSizing: "border-box",
                }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                viewport={{ once: true }}
            >
                <p style={{ margin: 0 }}>
                    Alle unsere Welpen werden mehrfach entwurmt, geimpft, gechipt
                    und mit einem EU-Heimtierausweis übergeben. Sie werden liebevoll
                    in häuslicher Umgebung aufgezogen und sind optimal auf das Leben
                    in einer Familie vorbereitet.
                </p>

                <motion.button
                    style={{
                        ...styles.button,
                        backgroundColor: primaryColor,
                        marginTop: "20px",
                        width: "min(100%, 260px)",
                        minHeight: 44,
                    }}
                    whileHover={{ scale: 1.05, backgroundColor: secondaryColor }}
                    whileTap={{ scale: 0.95 }}
                    onClick={onPuppyInquiry}
                >
                    Welpen anfragen
                </motion.button>
            </motion.div>
        </section>
    );
}

function InfoRow({ label, value }) {
    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "minmax(84px, 34%) minmax(0, 1fr)",
                gap: 10,
                fontSize: "clamp(0.88rem, 2.4vw, 0.95rem)",
                lineHeight: 1.45,
                minWidth: 0,
            }}
        >
            <span
                style={{
                    fontWeight: 800,
                    color: "#444",
                    overflowWrap: "anywhere",
                }}
            >
                {label}
            </span>

            <span
                style={{
                    color: "#333",
                    overflowWrap: "anywhere",
                    minWidth: 0,
                }}
            >
                {value}
            </span>
        </div>
    );
}